// All landing-page copy lives here so it can be reviewed and edited in one place.
// Stats come from vimbal's own published / reported figures. Keep "up to" qualifiers.

import videoCalm from "../vid/vimbal_video_five.mp4";

export const contact = {
  email: "hello@vimbal.ai",
  phone: "+61 498 579 188",
  phoneHref: "tel:+61498579188",
  phoneNote: "Australia",
  linkedin: "https://www.linkedin.com/in/joshuaarnall",
  linkedinLabel: "LinkedIn",
};

export const links = {
  register: "/register",
  // Web app sign-in. Empty hides the Log in button everywhere; set the URL when the app is live.
  login: "/login",
  loginLabel: "Log in",
};

// Web app sign-in. Accounts are issued by the team, so there is no self-service sign-up:
// the form checks details locally and, with no account service behind it yet, every attempt
// returns the incorrect-details message. Nothing typed here leaves the browser.
export const loginPage = {
  eyebrow: "Log in",
  titleLead: "Welcome back.",
  titleEmphasis: "Sign in to vimbal.",
  body: "Use the email and password vimbal sent you.",
  emailLabel: "Email",
  passwordLabel: "Password",
  submit: "Sign in",
  submitting: "Signing in…",
  forgot: "Forgotten your password?",
  forgotSubject: "vimbal password reset",
  errorTitle: "Incorrect email or password.",
  errorBody: "Check your details and try again. If you need your login resent, email hello@vimbal.ai.",
  noAccount: "Accounts are set up by the vimbal team when your platform access begins.",
  emailSubject: "vimbal web app access",
  emailCta: "Request access",
  registerCta: "Register interest",
};

// Register link that pre-selects an area of interest on the form.
export const registerFor = (slug) => (slug ? `/register?interest=${slug}` : links.register);

export const nav = [
  { label: "How it works", to: "/how-it-works" },
  { label: "Founding story", to: "/founder" },
];

// The roadmap answers "where is this going?", which matters to partners but reads as
// "not finished" in the top bar, so it lives in the footer only.
export const footerNav = [{ label: "Roadmap", to: "/how-it-works#roadmap" }];

export const hero = {
  // Four lines. The name leads so a search visitor knows whose page this is at a glance.
  titleBrand: "vimbal.",
  titleLead: "The Neuro-AI",
  titleEmphasis: "that expands",
  titleTail: "your mind.",
  body:
    "Building the world's first neuroadaptive learning engine, and the fastest non-invasive AI brain-computer interface. vimbal calms the brain's stress center so information flows into long-term memory: flow-state focus within minutes, and reading at up to 500 words per minute, with test scores lifted from a credit to a distinction.",
  bodyClose: "Where learning becomes neurological, not informational.",
  primaryCta: "Register interest",
  hintPointer: "Move across the waves to find flow",
  hintTouch: "Drag across the waves to find flow",
  flowLabel: "of flow state",
  // Brain state for each band, fastest to slowest (gamma → delta).
  states: ["High stress", "Daily work", "Calm focus", "Flow state", "Deep sleep"],
};

// Recognition, verified on connect.org (Class of 2025 Cool Companies, 30 companies).
export const award = {
  // Source: CONNECT Cool Companies, Class of 2025 (linked below).
  title: "Top 30 Early Stage Company",
  region: "Southern California",
  country: "USA",
  year: "2025",
  source: "Selected by CONNECT",
  origin: "Australian-founded",
  href: "https://connect.org/class-of-2025-cool-companies/",
};

export const reveal = {
  video: videoCalm,
  kicker: "Calm is where learning starts",
  line: "Shoulders drop. Breathing slows. The mind opens.",
};

export const marquee = [
  "Mental health",
  "Neurodivergence",
  "Spatial computing",
  "Deep focus within minutes",
  "Up to 500 words per minute",
  "Neuroadaptive learning",
  "Education",
  "Mining & workforce",
  "Defense",
  "Patent filed in 13+ jurisdictions",
];

// What the name stands for.
export const nameMeaning = {
  eyebrow: "What's in a name",
  full: "Virtual Immersive Mindfulness Based Accelerated Learning",
  letters: [
    { letter: "V", word: "Virtual", body: "A world you step into, wherever you are." },
    { letter: "I", word: "Immersive", body: "Sight and sound that shut out the noise." },
    { letter: "M", word: "Mindfulness", body: "Calm first, so the mind can open." },
    { letter: "B", word: "Based", body: "Grounded in neuroscience, not novelty." },
    { letter: "A", word: "Accelerated", body: "Reading and recall at speed." },
    { letter: "L", word: "Learning", body: "Knowledge that goes in, and stays." },
  ],
};

// The cognitive crisis, with public sources.
export const crisis = {
  eyebrow: "The problem",
  titleLead: "The world is drowning in information.",
  titleEmphasis: "The bottleneck is the brain.",
  body: "Smart devices keep the brain's stress center switched on. Attention is fragmenting, information keeps multiplying, and a stressed brain cannot move what it reads into memory.",
  stats: [
    { value: "275", label: "interruptions a day at work from meetings, email and chat: one every two minutes", source: "Microsoft Work Trend Index (2025)" },
    { value: "47 sec", label: "average time people stay focused on one screen before switching", source: "Gloria Mark, UC Irvine, Attention Span (2023)" },
    { value: "1 in 6", label: "people are neurodivergent, an estimated 15 to 20%, and too often underserved", source: "Doyle, British Medical Bulletin (2020)" },
  ],
};

export const manifesto =
  "AI is teaching the world to stop reading. Every shortcut hands a little more of our thinking away. vimbal turns that around, using AI to carry knowledge into the brain, not around it. Learning becomes neurological, not informational, and people read more, remember more, and grow.";

export const contrast = {
  eyebrow: "A different kind of AI",
  titleLead: "Most AI is a crutch.",
  titleEmphasis: "vimbal is a neuro-trellis.",
  body:
    "Lean on a crutch long enough and the muscle weakens. A trellis works the other way: it supports you while you grow higher on your own.",
  down: {
    label: "AI as a crutch",
    stops: ["Skip the reading", "Skim the summary", "Forget the source", "Ask AI again"],
  },
  up: {
    label: "AI as a trellis",
    stops: ["Calm the mind", "Read deeply", "Remember", "Grow"],
  },
};

export const stats = [
  { value: 40, suffix: "%", prefix: "up to", label: "less time per induction, on their own material" },
  { value: 81, suffix: "%", prefix: "65% →", label: "average safety induction test score, a credit lifted to a distinction" },
  { value: 66, suffix: "%", prefix: "up to", label: "faster reading on the same material" },
  { value: 500, suffix: "", prefix: "up to", label: "words per minute, with flow reached from around 300" },
];

export const impactLead =
  "Thirty sensory elements in 55 tuned combinations, protected by a patent filed in more than 13 jurisdictions.";

// vimbal's cognitive ascent (what the MVP does today). The AI neuroscientist is a later release.
export const steps = [
  {
    key: "enter",
    title: "Enter",
    body: "Step into vimbal in XR. The immersive interface blocks outside sensory noise, whether that's everyday overload, anxiety or an ADHD brain in top gear.",
    hud: "STAGE 1 · ENTER XR",
    state: "Stress response high",
  },
  {
    key: "reset",
    title: "Reset",
    body: "Audio-visual disconnection lifts the overload. A calming world, like diving on the Great Barrier Reef, brings you into the present.",
    hud: "STAGE 2 · COGNITIVE RESET",
    state: "Settling",
  },
  {
    key: "drop-in",
    title: "Drop in",
    body: "A meditative, alpha-state calm arrives rapidly. The body visibly relaxes: shoulders release, hands drop, the head tilts.",
    hud: "STAGE 3 · MEDITATIVE DROP-IN",
    state: "Deep calm",
  },
  {
    key: "activate",
    title: "Activate",
    body: "vimbal's patent-filed cognitive engine takes over, synchronizing rhythmic motion, REM-inspired pacing, sound and color as you read.",
    hud: "STAGE 4 · COGNITIVE ENGINE",
    state: "Learning",
  },
  {
    key: "flow",
    title: "Flow",
    body: "Everything meets at the convergence point: hyper-focused cognitive flow, the stress center calm and a clearer path to memory, while you read at 300 to 500 words per minute.",
    hud: "STAGE 5 · COGNITIVE FLOW",
    state: "In flow",
  },
];

// Headsets the MVP runs on today.
export const platforms = {
  label: "Runs today on",
  devices: ["Meta Quest 3", "Apple Vision Pro", "Web app"],
};

// "Where vimbal runs": the picture and the caption describe the same thing.
// the same thing. Today = a person in the headset customers actually use; Next = vimbal's own
// glasses; Meta's announcement becomes a market note beneath, not the caption.
export const devicesNew = {
  eyebrow: "Where vimbal runs",
  title: "On headsets today, everyday glasses next",
  thread: "Today \u2192 Next",
  today: {
    label: "Today",
    tag: "Meta Quest 3 \u00b7 Apple Vision Pro \u00b7 Web",
    alt: "A person using vimbal in a Meta Quest headset at an office desk.",
    body: "Full immersion on Meta Quest 3 and Apple Vision Pro, plus a web app, on the documents you already have.",
  },
  next: {
    label: "Next",
    tag: "In development",
    alt: "vimbal's lightweight glasses concept, turning slowly.",
    body: "vimbal's own lightweight glasses, in development: the same cognitive engine, without the headset.",
  },
  market: "Meta's VR Glasses, announced September 2026 at about 100 grams, bring the form factor to market in 2027.",
  note: "Product render \u00b7 Apple Vision Pro, Meta Quest and Meta VR Glasses are trademarks of their owners",
};

export const stepsNext =
  "From distraction to deep focus within minutes, then into flow. Coming in Release 3: an AI neuroscientist that reads brain signals and adapts the environment in real time.";

export const explore = {
  eyebrow: "Who it's for",
  titleLead: "Built first for minds that",
  titleEmphasis: "work differently.",
  body: "Mental health and neurodivergence lead everything we build, and the same science speeds up learning and training for everyone.",
};

export const proof = {
  eyebrow: "Proof",
  titleLead: "Results you can",
  titleEmphasis: "measure, and see.",
  body: "Measured on site with an Australian engineering and mining services company of several hundred staff, on their own induction material. People read faster, scored higher and took less time to onboard.",
  signsTitle: "What calm looks like",
  signs: ["Shoulders release their tension", "Arms fall into the lap", "Hands drop to the side", "The head tilts", "Breathing slows and deepens"],
  signsNote: "These visible signs typically appear within minutes, as the brain's stress response lets go.",
  observationsTitle: "What we saw on site",
  observations: [
    "Flow reached at around 300+ words per minute",
    "Less cognitive load and fatigue during onboarding",
    "Better recall and test performance",
    "More engagement than tick-and-flick inductions",
    "Positive responses from ADHD, autistic and ESL participants",
  ],
};

// Retention band after the speed card: faster only matters if it sticks.
// Figures from the 2024 mining pilot (see pilotResults in verticalContent.js).
export const retention = {
  eyebrow: "Memory",
  title: "And it stays.",
  value: 65,
  prefix: "up to ",
  suffix: "%",
  label: "higher test score on the same material.",
  source: "Measured on site, Australian engineering and mining services",
};

export const speedGap = {
  eyebrow: "AI-to-brain",
  titleLead: "AI writes faster than",
  titleEmphasis: "we can read.",
  body: "Large language models write at thousands of words a minute. An average adult reads 238. Measured on site, vimbal lifted the group average to 354 words per minute and the fastest round to 539, on their own induction material. The roadmap target is 700 to 900. The brain is the bottleneck. vimbal is building the fastest non-invasive AI brain-computer interface, and these are the numbers so far.",
  // Human reading, ascending. `min`/`max` in words per minute; a range shows a solid bar to `min`, lighter to `max`.
  rows: [
    { label: "Neurodivergent reader", note: "1", min: 30, max: 110, display: "30–110 wpm", kind: "human" },
    { label: "Adult with low literacy", note: "1", max: 100, display: "100 wpm", kind: "human" },
    { label: "Child (Year 4)", note: "1", max: 133, display: "133 wpm", kind: "human" },
    { label: "Average adult", note: "2", max: 238, display: "238 wpm", kind: "average" },
    { label: "Measured in vimbal, average", note: "4", max: 354, display: "354 wpm", kind: "vimbal" },
    { label: "Fastest reader in vimbal", note: "4", max: 455, display: "455 wpm", kind: "vimbal" },
    { label: "Fastest round in vimbal", note: "4", max: 539, display: "539 wpm", kind: "vimbal" },
    { label: "Projected in vimbal, roadmap", note: "5", min: 700, max: 900, display: "700–900 wpm", kind: "vimbal" },
  ],
  // LLM generation is far beyond this scale, so it is shown as text.
  beyond: {
    title: "Off this chart: AI generation",
    items: [
      { label: "Leading AI models", note: "3", display: "2,000–9,000 wpm" },
      { label: "Fastest AI models", note: "3", display: "65,000 wpm" },
    ],
  },
  sources: [
    "¹ Hasbrouck & Tindal (2017), used as an estimate.",
    "² Brysbaert (2019), adult silent reading.",
    "³ Artificial Analysis (Sept 2026), 50–200 and about 1,500 tokens/sec at 0.75 words per token.",
    "⁴ vimbal, measured on site: group average, fastest reader's three-round average, fastest single round.",
    "⁵ Roadmap target with the AI neuroscientist, not yet measured.",
    "Multipliers compare vimbal's fastest recorded round (539 wpm) with each group's typical speed.",
  ],
  // vimbal's fastest recorded round, used for the headroom bars and multipliers.
  vimbalPace: 539,
  multiplierLabel: "vimbal pace",
};

// Build stages. `current` marks where vimbal is today.
export const roadmap = {
  eyebrow: "Build stage",
  titleLead: "Where we are",
  titleEmphasis: "on the climb.",
  body: "A working cognitive engine today, and a clear path to the cognitive operating system of spatial computing.",
  hint: "Select a stage to see what's in it",
  currentIndex: 0,
  stages: [
    {
      short: "Now",
      when: "Today",
      nodeName: "Cognitive engine MVP",
      title: "The cognitive engine, live",
      body: "A working, patent-filed cognitive engine running on XR headsets and the web, with fast PDF upload, neurodivergent modes, paying NDIS participants and results measured on site in mining.",
      items: ["XR and web reading, in sync", "Neurodivergent modes", "Paying participants in Australia's National Disability Insurance Scheme (NDIS); measured on site in mining"],
    },
    {
      short: "Release 1",
      when: "2025–26",
      nodeName: "NDIS learning platform",
      title: "NDIS learning platform",
      body: "Home onboarding with leased headsets, built-in comprehension testing, and NDIS registration with plan-manager tooling.",
      items: ["Home onboarding with leased headsets", "Built-in comprehension testing", "NDIS registration and plan-manager tooling"],
    },
    {
      short: "Release 2",
      when: "2026",
      nodeName: "AI document intelligence",
      title: "AI document intelligence",
      body: "AI restructures any document into XR-ready learning sequences, with more file formats, enterprise dashboards and learning-platform integrations.",
      items: ["Any document, restructured by AI", "Multi-format ingestion", "Enterprise dashboards and integrations"],
    },
    {
      short: "Release 3",
      when: "2026–27",
      nodeName: "AI tutor & neuroscientist",
      title: "AI tutor and AI neuroscientist",
      body: "A conversational AI tutor and real-time cognitive adjustment from brain signals, with fully adaptive XR environments and a mobile companion.",
      items: ["Conversational AI tutor", "Real-time cognitive adjustment", "Mobile companion"],
    },
    {
      short: "Vision",
      when: "2030+",
      nodeName: "Cognitive OS & XR glasses",
      title: "The cognitive operating system",
      body: "Lightweight XR glasses with built-in brain sensing, an embedded AI tutor and neuroscientist, and personalized flow-state induction: a seamless brain–computer interface.",
      items: ["Lightweight XR glasses", "Built-in brain sensing", "Personalized flow-state induction"],
    },
  ],
};

export const demo = {
  eyebrow: "Try it",
  titleLead: "Feel the",
  titleEmphasis: "speed.",
  body:
    "Read about vimbal one word at a time. Press play, then slide the speed up towards 600 words per minute and notice how your focus follows.",
  note: "A simple speed-reading preview on a flat screen. Inside vimbal, immersion, sound, color and motion take this much further.",
  presets: [
    { wpm: 150, label: "Average reader" },
    { wpm: 300, label: "AI output" },
    { wpm: 500, label: "vimbal readers" },
    { wpm: 600, label: "Top speed" },
  ],
  passage:
    "Welcome to vimbal. Virtual Immersive Mindfulness Based Accelerated Learning. Most technology competes for your attention. vimbal gives it back. First, an immersive world shuts out the noise. Your breathing slows. Your shoulders drop. The brain's stress center begins to calm, and a clearer path opens to memory. Then the reading begins. Sound, color and motion move with your pace, so your focus holds steady as the words speed up. On site in mining, people read faster, remembered more and scored higher. Many readers keep up at three hundred, four hundred, even five hundred words per minute. Notice that you are still following along right now. That is what flow feels like. Welcome to learning that grows your mind.",
};

export const cta = {
  eyebrow: "Patent filed in 13+ jurisdictions",
  title: "Let's lift the world's",
  titleEmphasis: "minds, not replace them.",
  body:
    "Be first to try vimbal, or bring it to your school, clinic, mine site or team.",
  primary: "Register interest",
};
